import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  FaBolt, FaFistRaised, FaShieldAlt, FaHeart, FaBullseye, FaRedo,
  FaEye, FaEyeSlash, FaArrowLeft, FaSearch, FaExclamationTriangle, FaInfoCircle,
} from 'react-icons/fa';
import { startCase } from 'lodash';
import styled from 'styled-components';
import { cutoutUrlFor } from '../data/characterCutouts';
import { API_URL } from '../socket';
import { resolveMoveGifUrl } from '../utils/moveGifUrl';
import { MOVE_GIF_MAP } from '../utils/moveGifMap';

/* ─────────────── Character roster — mass-poster skin ───────────────
   Each character is a fight-program card: ink panel, cut corners, amber
   rating stamp, Oswald name, mono stat readouts. The signature-moves list
   mirrors the in-arena move tiles for visual continuity. */

// ────────── page shell ──────────
const CharacterPageContainer = styled.div`
  min-height: 100vh;
  background:
    radial-gradient(ellipse 70% 50% at 50% -10%, rgba(255, 177, 60, 0.05) 0%, transparent 70%),
    var(--color-ink-950);
  padding: clamp(24px, 5vh, 56px) var(--space-6) var(--space-10);
  color: var(--color-cream-100);
  position: relative;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: var(--space-10);
`;

/* Display-italic title with flanking slashes — same lockup as the end card
   and leaderboard so every secondary page feels authored. */
const PageTitle = styled.h1`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin: 0 0 var(--space-3);
  font-family: var(--font-display);
  font-size: clamp(44px, 6vw, 84px);
  font-weight: var(--weight-bold);
  font-style: italic;
  letter-spacing: -0.01em;
  line-height: 0.95;
  text-transform: uppercase;
  color: var(--color-amber-500);
  text-shadow: 0 3px 0 var(--color-ink-950), 0 0 36px rgba(255, 177, 60, 0.3);

  &::before, &::after {
    content: '////';
    font-size: 0.32em;
    letter-spacing: -0.08em;
    opacity: 0.8;
  }
`;

const PageSubtitle = styled.p`
  margin: 0 auto;
  max-width: 720px;
  font-family: var(--font-heading);
  font-size: 12px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-cream-300);
`;

/* How to read the roster — sits under the title so first-time players get context. */
const RosterGuide = styled.aside`
  max-width: 920px;
  margin: 0 auto var(--space-8);
  padding: var(--space-5) var(--space-5) var(--space-4);
  background: rgba(8, 7, 10, 0.72);
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-card);
  color: var(--color-cream-200);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 2px;
    background: var(--color-amber-500);
    opacity: 0.95;
  }
`;

const RosterGuideHead = styled.h2`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 var(--space-3);
  font-family: var(--font-heading);
  font-size: 11px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-amber-500);

  svg {
    flex-shrink: 0;
    opacity: 0.9;
  }
`;

const RosterGuideBody = styled.div`
  font-family: var(--font-body);
  font-size: 13px;
  line-height: 1.55;
  color: var(--color-cream-200);

  strong {
    color: var(--color-cream-100);
    font-weight: var(--weight-semibold);
  }

  ul {
    margin: var(--space-3) 0 0;
    padding-left: 1.2rem;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  li::marker {
    color: var(--color-amber-500);
  }
`;

/* ─── sticky header bar ──────────────────────────────────────────
   Single row: back link + search + sort, sits just under the page
   title so the user can navigate and filter without scrolling to
   a corner. Sticky so it stays reachable on long rosters. */
const ControlsBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: var(--space-4);
  max-width: 1440px;
  margin: 0 auto var(--space-6);
  padding: 12px 16px;
  background: linear-gradient(180deg,
    rgba(8, 7, 10, 0.92) 0%,
    rgba(8, 7, 10, 0.76) 100%);
  backdrop-filter: blur(8px);
  border: 1px solid var(--color-ink-800);
  clip-path: var(--clip-card);

  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 2px;
    background: var(--color-amber-500);
    opacity: 0.9;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  font-family: var(--font-heading);
  font-size: 11px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-cream-200);
  background: var(--color-ink-900);
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-button);
  text-decoration: none;
  transition: color var(--dur-fast) var(--ease-out),
              border-color var(--dur-fast) var(--ease-out);

  &:hover {
    color: var(--color-amber-500);
    border-color: var(--color-amber-500);
    text-decoration: none;
  }
`;

const SearchWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  svg {
    position: absolute;
    left: 12px;
    color: var(--color-cream-400);
    pointer-events: none;
    font-size: 12px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 14px 10px 32px;
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--color-cream-100);
  background: var(--color-ink-900);
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-button);
  outline: none;
  transition: border-color var(--dur-fast) var(--ease-out);

  &::placeholder {
    color: var(--color-cream-400);
    letter-spacing: 0.04em;
  }
  &:focus {
    border-color: var(--color-amber-500);
  }
`;

const SortWrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--color-ink-900);
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-button);
  padding: 4px 6px 4px 12px;
`;

const SortLabel = styled.span`
  font-family: var(--font-heading);
  font-size: 10px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-cream-400);
`;

const SortSelect = styled.select`
  appearance: none;
  border: none;
  background: transparent;
  color: var(--color-cream-100);
  font-family: var(--font-heading);
  font-size: 11px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  padding: 6px 24px 6px 6px;
  cursor: pointer;
  outline: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3e%3cpath fill='%23E8DFC8' d='M5 7L1 3h8z'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 6px center;

  option {
    background: var(--color-ink-900);
    color: var(--color-cream-100);
  }
`;

const CharacterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: var(--space-6);
  max-width: 1440px;
  margin: 0 auto;
  padding: var(--space-2);
`;

// ────────── character card ──────────
const CharacterCard = styled(motion.div)`
  position: relative;
  background: linear-gradient(180deg, var(--color-ink-900) 0%, var(--color-ink-950) 100%);
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-card);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  transition: border-color var(--dur-base) var(--ease-out),
              transform var(--dur-base) var(--ease-out);

  /* amber top rail */
  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 3px;
    background: var(--color-amber-500);
    z-index: 3;
  }

  &:hover {
    border-color: var(--color-amber-500);
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  position: relative;
  height: 320px;
  background:
    radial-gradient(ellipse at 50% 30%, rgba(193, 39, 45, 0.22) 0%, transparent 70%),
    linear-gradient(180deg, var(--color-red-900) 0%, var(--color-ink-900) 80%);
  overflow: hidden;
  border-bottom: 1px solid var(--color-ink-700);
`;

/* Portrait well for the full-body cutout. Tall 3:4 frame matches the
   source PNG aspect so the whole character fits with `object-fit: contain`
   and no odd cropping. Amber edge keys the card to the mass-poster palette. */
const CharacterImageSection = styled.div`
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  width: 210px;
  height: 280px;
  /* No backdrop panel — cutouts are transparent and should float against
     the card header's radial glow. No clip/border here either; the
     character silhouette IS the frame. */
`;

const CharacterImage = styled.img`
  width: 100%;
  height: 100%;
  /* contain (not cover) — cutouts are pre-framed 3:4 transparent PNGs.
     Using cover would crop off head or feet on most of them. */
  object-fit: contain;
  object-position: center bottom;
  filter: drop-shadow(0 10px 24px rgba(8, 7, 10, 0.55));
`;

/* Overall rating — amber hex stamp in the corner, reads like a fight
   program's headline stat. */
const CharacterRating = styled.div`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-amber-500);
  color: var(--color-ink-950);
  font-family: var(--font-display);
  font-style: italic;
  font-size: 20px;
  font-weight: var(--weight-bold);
  letter-spacing: -0.02em;
  clip-path: var(--clip-button);
  box-shadow: var(--shadow-md);
  z-index: 4;
`;

/* Role badge — derived from the character's highest stat (see
   `deriveRole` below). Sits in the top-left corner opposite the
   amber rating stamp so the two read as a matched pair of
   fight-program tags. */
const RoleBadge = styled.div`
  position: absolute;
  top: 14px;
  left: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  font-family: var(--font-heading);
  font-size: 10px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-cream-100);
  background: rgba(8, 7, 10, 0.72);
  border: 1px solid var(--color-ink-700);
  border-left: 3px solid ${p => p.$accent || 'var(--color-amber-500)'};
  clip-path: var(--clip-button);
  backdrop-filter: blur(6px);
  z-index: 4;
`;

const CharacterName = styled.h2`
  position: absolute;
  bottom: 12px;
  left: 0;
  right: 0;
  margin: 0;
  padding: 0 var(--space-4);
  font-family: var(--font-display);
  font-size: clamp(20px, 2.4vw, 28px);
  font-weight: var(--weight-bold);
  font-style: italic;
  letter-spacing: -0.01em;
  line-height: 1;
  text-transform: uppercase;
  text-align: center;
  color: var(--color-cream-100);
  text-shadow: 0 2px 0 var(--color-ink-950), 0 0 16px rgba(8, 7, 10, 0.8);
  z-index: 4;
`;

const CardBody = styled.div`
  padding: var(--space-5);
`;

const StatsSection = styled.div`
  margin-bottom: var(--space-5);
`;

const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 0 var(--space-3);
  padding-bottom: 6px;
  font-family: var(--font-heading);
  font-size: 11px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-amber-500);
  border-bottom: 1px solid var(--color-ink-700);

  &::after {
    content: '////';
    font-family: var(--font-display);
    font-style: italic;
    font-size: 10px;
    letter-spacing: -0.08em;
    opacity: 0.7;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
`;

const StatCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 8px 4px;
  background: rgba(8, 7, 10, 0.5);
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-button);
`;

const StatIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-cream-300);
  font-size: 12px;
`;

const StatValue = styled.div`
  font-family: var(--font-mono);
  font-size: 16px;
  font-weight: var(--weight-bold);
  color: ${props => props.color || 'var(--color-cream-100)'};
  letter-spacing: 0.02em;
  line-height: 1;
`;

const StatLabel = styled.div`
  font-family: var(--font-heading);
  font-size: 9px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-cream-400);
`;

const MovesSection = styled.div`
  margin-top: var(--space-4);
`;

const MovesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 380px;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: var(--color-ink-900); }
  &::-webkit-scrollbar-thumb {
    background: var(--color-ink-600);
    &:hover { background: var(--color-amber-500); }
  }
`;

// ────────── move card ──────────
const MoveCard = styled(motion.div)`
  position: relative;
  padding: 12px 14px 10px;
  background: rgba(8, 7, 10, 0.55);
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-button);
  cursor: default;
  transition: border-color var(--dur-fast) var(--ease-out);

  /* Amber accent stripe on the left, like the in-arena move tiles */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: var(--color-amber-500);
  }

  &:hover {
    border-color: var(--color-amber-500);
  }
`;

const MoveHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const MoveName = styled.span`
  font-family: var(--font-heading);
  font-size: 13px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  color: var(--color-cream-100);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
`;

const MoveType = styled.span`
  flex-shrink: 0;
  padding: 2px 8px;
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-amber-500);
  background: rgba(255, 177, 60, 0.1);
  border: 1px solid var(--color-amber-500);
  clip-path: var(--clip-button);
`;

const MoveKindRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: -2px 0 10px;
  flex-wrap: wrap;
`;

const MoveKindBadge = styled.span`
  font-family: var(--font-heading);
  font-size: 9px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  padding: 3px 8px;
  clip-path: var(--clip-button);
  border: 1px solid currentColor;
  color: ${(p) =>
    p.$kind === 'attack' ? 'var(--color-red-400)' : 'var(--color-cream-200)'};
  background: ${(p) =>
    p.$kind === 'attack'
      ? 'rgba(193, 39, 45, 0.12)'
      : 'rgba(232, 223, 200, 0.08)'};
`;

/* Native tooltip via title; focusable for keyboard users. */
const StatusMoveInfoBtn = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 2px;
  padding: 4px;
  cursor: help;
  color: var(--color-cream-400);
  border-radius: 2px;
  line-height: 0;
  transition: color var(--dur-fast) var(--ease-out);

  &:hover,
  &:focus-visible {
    color: var(--color-amber-500);
  }

  &:focus-visible {
    outline: 2px solid var(--color-amber-500);
    outline-offset: 2px;
  }

  svg {
    font-size: 13px;
  }
`;

const MoveStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  margin-bottom: 8px;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 5px 2px;
  background: var(--color-ink-900);
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-button);
`;

const MoveEffects = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 8px;
`;

/* Single-color chip — previous multi-color mapping (purple/teal/gold) was
   off-theme. Effect semantics now map to either amber (positive) or red
   (negative) so the palette stays mass-poster. */
const EffectTag = styled.span`
  padding: 3px 7px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: var(--weight-semibold);
  letter-spacing: 0.02em;
  color: ${props =>
        props.effect === 'damage' || props.effect === 'debuff'
            ? 'var(--color-red-500)'
            : 'var(--color-amber-500)'};
  background: ${props =>
        props.effect === 'damage' || props.effect === 'debuff'
            ? 'rgba(193, 39, 45, 0.1)'
            : 'rgba(255, 177, 60, 0.1)'};
  border: 1px solid currentColor;
  clip-path: var(--clip-button);
`;

/* Compact "peek" button that collapses the move GIF by default. GIFs
   loop at ~100 KB each and were eating vertical space on every card;
   moving them behind a click means the roster scans as a stat sheet
   first, with media on demand. */
const GifToggleButton = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  font-family: var(--font-heading);
  font-size: 10px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-cream-300);
  background: var(--color-ink-900);
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-button);
  cursor: pointer;
  transition: color var(--dur-fast) var(--ease-out),
              border-color var(--dur-fast) var(--ease-out);

  &:hover {
    color: var(--color-amber-500);
    border-color: var(--color-amber-500);
  }

  &:focus-visible {
    outline: 2px solid var(--color-amber-500);
    outline-offset: 2px;
  }

  svg { font-size: 11px; }
`;

const MoveAnimation = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  margin-top: 8px;
  background: var(--color-ink-950);
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-button);
  overflow: hidden;
`;

const MoveGif = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;

  &::after {
    content: '';
    width: 36px;
    height: 36px;
    border: 3px solid rgba(255, 177, 60, 0.18);
    border-top-color: var(--color-amber-500);
    border-radius: 50%;
    animation: char-list-spin 0.9s linear infinite;
  }

  @keyframes char-list-spin {
    to { transform: rotate(360deg); }
  }
`;

/* Non-happy-path container — shared by network error and empty search
   result. Mass-poster ink panel with an amber rail and a centered
   message so the page still feels authored when there's nothing to
   show. */
const StatusPanel = styled.div`
  max-width: 560px;
  margin: 80px auto;
  padding: 32px 28px;
  text-align: center;
  background: linear-gradient(180deg, var(--color-ink-900) 0%, var(--color-ink-950) 100%);
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-card);
  box-shadow: var(--shadow-lg);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 3px;
    background: var(--color-amber-500);
  }
`;

const StatusIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: 0 auto 14px;
  background: rgba(193, 39, 45, 0.12);
  color: var(--color-red-500);
  border: 1px solid var(--color-red-500);
  clip-path: var(--clip-button);
  font-size: 18px;
`;

const StatusTitle = styled.h2`
  margin: 0 0 6px;
  font-family: var(--font-display);
  font-style: italic;
  font-size: 26px;
  font-weight: var(--weight-bold);
  letter-spacing: -0.01em;
  text-transform: uppercase;
  color: var(--color-amber-500);
`;

const StatusDesc = styled.p`
  margin: 0 0 18px;
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--color-cream-300);
`;

const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  font-family: var(--font-heading);
  font-size: 11px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-ink-950);
  background: var(--color-amber-500);
  border: 1px solid var(--color-amber-500);
  clip-path: var(--clip-button);
  cursor: pointer;
  transition: filter var(--dur-fast) var(--ease-out),
              transform var(--dur-fast) var(--ease-out);

  &:hover {
    filter: brightness(1.08);
    transform: translateY(-1px);
  }
`;

const STATUS_MOVE_TOOLTIP =
  'Status moves usually deal no direct damage. They set up your fighter or weaken the opponent (stat boosts, debuffs, sleep, multi-hit setup, etc.). In battle, each move still spends uses—even on a miss.';

function normalizeMoveEffects(move) {
  const e = move.effects;
  if (!e) return [];
  if (Array.isArray(e)) return e;
  if (typeof e === 'object') return Object.values(e);
  return [];
}

function describeEffect(effect) {
  if (!effect || typeof effect !== 'object') return '';
  if (effect.description) return String(effect.description);
  if (effect.type === 'user_stat_change') {
    const dir = effect.change === 'increase' ? 'Raises' : 'Lowers';
    const v = typeof effect.value === 'string' ? effect.value : `by ${effect.value}`;
    return `${dir} this fighter’s ${startCase(effect.field || '')} ${v}.`;
  }
  if (effect.type === 'opponent_stat_change') {
    const dir = effect.change === 'increase' ? 'Raises' : 'Lowers';
    const v = typeof effect.value === 'string' ? effect.value : `by ${effect.value}`;
    return `${dir} the opponent’s ${startCase(effect.field || '')} ${v}.`;
  }
  if (effect.type === 'move_repeat') {
    return `Hits multiple times in one turn (${effect.repeat ?? '?'} strikes).`;
  }
  if (effect.type === 'move_change') {
    return `Changes ${startCase(effect.field || 'move')} (${effect.change || '—'}).`;
  }
  if (effect.type === 'special' && effect.effect === 'sleep') {
    return `Inflicts sleep for about ${effect.turns ?? '?'} turns.`;
  }
  return effect.type ? startCase(String(effect.type).replace(/_/g, ' ')) : '';
}

/* Single move row. Extracted so the GIF toggle can own its own local
   state (`showGif`) without re-rendering every sibling move in the
   character card. */
const MoveRow = ({ move, index }) => {
  const [showGif, setShowGif] = useState(false);
  const [gifFailed, setGifFailed] = useState(false);
  const effectsList = normalizeMoveEffects(move);
  const isAttack = move.power > 0;
  const maxUses = Number(move.limit) > 0 ? move.limit : 10;

  return (
    <MoveCard
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(0.3 + index * 0.06, 0.6), duration: 0.25 }}
    >
      <MoveHeader>
        <MoveName>{move.name}</MoveName>
        <MoveType>
          {move.type || 'Normal'}
        </MoveType>
      </MoveHeader>

      <MoveKindRow>
        <MoveKindBadge $kind={isAttack ? 'attack' : 'status'}>
          {isAttack ? 'Damage move' : 'Status move'}
        </MoveKindBadge>
        {!isAttack && (
          <StatusMoveInfoBtn
            type="button"
            title={STATUS_MOVE_TOOLTIP}
            aria-label={STATUS_MOVE_TOOLTIP}
          >
            <FaInfoCircle aria-hidden />
          </StatusMoveInfoBtn>
        )}
      </MoveKindRow>

      <MoveStats>
        <StatItem>
          <StatIcon><FaBolt /></StatIcon>
          <StatValue color="var(--color-red-500)">
            {isAttack ? move.power : '—'}
          </StatValue>
          <StatLabel>PWR</StatLabel>
        </StatItem>
        <StatItem>
          <StatIcon><FaBullseye /></StatIcon>
          <StatValue>{move.accuracy === true ? 100 : move.accuracy || 100}%</StatValue>
          <StatLabel>ACC</StatLabel>
        </StatItem>
        <StatItem title="Max times this move can be chosen in a match (from the database).">
          <StatIcon><FaRedo /></StatIcon>
          <StatValue>{maxUses}</StatValue>
          <StatLabel>Max uses</StatLabel>
        </StatItem>
      </MoveStats>

      {effectsList.length > 0 && (
        <MoveEffects>
          {effectsList.map((effect, effectIdx) => (
            <EffectTag key={effectIdx} effect={effect.type}>
              {describeEffect(effect)}
            </EffectTag>
          ))}
        </MoveEffects>
      )}

      {!gifFailed && (
        <GifToggleButton
          type="button"
          onClick={() => setShowGif((v) => !v)}
          aria-expanded={showGif}
          aria-label={showGif ? `Hide ${move.name} preview` : `Preview ${move.name}`}
        >
          {showGif ? <FaEyeSlash /> : <FaEye />}
          {showGif ? 'Hide preview' : 'Preview'}
        </GifToggleButton>
      )}

      <AnimatePresence initial={false}>
        {showGif && !gifFailed && (
          <MoveAnimation
            key="gif"
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: 100, marginTop: 8 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            transition={{ duration: 0.2 }}
          >
            <MoveGif
              src={resolveMoveGifUrl(move, MOVE_GIF_MAP)}
              alt={`${move.name} animation`}
              onError={() => setGifFailed(true)}
            />
          </MoveAnimation>
        )}
      </AnimatePresence>
    </MoveCard>
  );
};

/* Derive a single-word "role" from the character's stat spread so the
   roster can be scanned at a glance. Picks the dominant stat; if the
   top and runner-up are within 8 points we call it balanced. Tuned so
   most characters land on a real role rather than everyone being
   "All-rounder". */
const deriveRole = ({ health, attack, defense, speed }) => {
  const pairs = [
    ['Bruiser', attack, 'var(--color-red-500)'],
    ['Tank', defense, 'var(--color-cream-200)'],
    ['Speedster', speed, 'var(--color-amber-500)'],
    ['Juggernaut', health, 'var(--color-red-400)'],
  ];
  pairs.sort((a, b) => b[1] - a[1]);
  const [top, second] = pairs;
  if (top[1] - second[1] < 8) {
    return { label: 'All-rounder', accent: 'var(--color-cream-300)' };
  }
  return { label: top[0], accent: top[2] };
};

const CharacterCardComponent = ({ character, index }) => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const overallRating = Math.round((character.health + character.attack + character.defense + character.speed) / 4);
  const role = deriveRole(character);

  /* Prefer the new local cutout PNG; fall back to the server-hosted
     thumbnail for any character that isn't in `characterCutouts.js`. */
  const portraitSrc = cutoutUrlFor(character) || character.image || '/placeholder-character.jpg';

  return (
    <CharacterCard
      ref={ref}
      initial={{ opacity: 0, y: 50, rotateX: -15 }}
      animate={inView ? { opacity: 1, y: 0, rotateX: 0 } : { opacity: 0, y: 50, rotateX: -15 }}
      transition={{ 
        duration: 0.8, 
        delay: index * 0.1,
        type: "spring",
        stiffness: 100
      }}
    >
      <CardHeader>
        <RoleBadge $accent={role.accent}>{role.label}</RoleBadge>
        <CharacterRating>{overallRating}</CharacterRating>

        <CharacterImageSection>
          <CharacterImage
            src={portraitSrc}
            alt={character.name}
            onError={(e) => {
              if (e.target.src !== window.location.origin + '/placeholder-character.jpg') {
                e.target.src = '/placeholder-character.jpg';
              }
            }}
          />
        </CharacterImageSection>

        <CharacterName>{character.name}</CharacterName>
      </CardHeader>
      
      <CardBody>
        <StatsSection>
          <SectionTitle>Battle Stats</SectionTitle>
          <StatsGrid>
            <StatCard>
              <StatIcon><FaHeart color="var(--color-red-500)" /></StatIcon>
              <StatValue>{character.health}</StatValue>
              <StatLabel>HP</StatLabel>
            </StatCard>
            <StatCard>
              <StatIcon><FaFistRaised color="var(--color-amber-500)" /></StatIcon>
              <StatValue>{character.attack}</StatValue>
              <StatLabel>ATK</StatLabel>
            </StatCard>
            <StatCard>
              <StatIcon><FaShieldAlt color="var(--color-cream-200)" /></StatIcon>
              <StatValue>{character.defense}</StatValue>
              <StatLabel>DEF</StatLabel>
            </StatCard>
            <StatCard>
              <StatIcon><FaBolt color="var(--color-amber-500)" /></StatIcon>
              <StatValue>{character.speed}</StatValue>
              <StatLabel>SPD</StatLabel>
            </StatCard>
          </StatsGrid>
        </StatsSection>

        {character.availableMoves && character.availableMoves.length > 0 && (
          <MovesSection>
            <SectionTitle>Move kit (reference)</SectionTitle>
            <MovesContainer>
              {character.availableMoves.map((move, idx) => (
                <MoveRow
                  key={move._id || idx}
                  move={move}
                  index={idx}
                />
              ))}
            </MovesContainer>
          </MovesSection>
        )}
      </CardBody>
    </CharacterCard>
  );
};

/* Sort presets exposed in the ControlsBar. Keyed to a stable `id` so the
   <select> value round-trips cleanly; `fn` is a comparator used by the
   memoized visible list below. */
const SORT_OPTIONS = [
  { id: 'rating', label: 'Rating', fn: (a, b) => statAvg(b) - statAvg(a) },
  { id: 'name', label: 'Name A–Z', fn: (a, b) => a.name.localeCompare(b.name) },
  { id: 'attack', label: 'Attack', fn: (a, b) => b.attack - a.attack },
  { id: 'defense', label: 'Defense', fn: (a, b) => b.defense - a.defense },
  { id: 'speed', label: 'Speed', fn: (a, b) => b.speed - a.speed },
  { id: 'health', label: 'Health', fn: (a, b) => b.health - a.health },
];

const statAvg = (c) => (c.health + c.attack + c.defense + c.speed) / 4;

const RosterGuideBlock = () => (
  <RosterGuide>
    <RosterGuideHead>
      <FaInfoCircle aria-hidden />
      How to read this page
    </RosterGuideHead>
    <RosterGuideBody>
      <p>
        In <strong>rated 4v4</strong>, each player gets four random fighters. Your active fighter brings{' '}
        <strong>four moves</strong> drawn from that character's pool for that match.
      </p>
      <ul>
        <li>
          <strong>Damage vs status:</strong> damage moves reduce HP; status moves focus on buffs, debuffs,
          sleep, multi-hit setup, or other effects instead of direct damage.
        </li>
        <li>
          <strong>Max uses:</strong> the number in the stat row is stored on each move in the database and is
          exactly what you get in rated 4v4 (uses are spent even on a miss).
        </li>
        <li>
          <strong>Effect tags:</strong> each line is what the move tries to do in battle, rewritten for clarity when
          the database only had shorthand.
        </li>
      </ul>
    </RosterGuideBody>
  </RosterGuide>
);

const CharacterListPage = () => {
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [sortId, setSortId] = useState('rating');

  useEffect(() => {
    fetchCharacters();
  }, []);

  const fetchCharacters = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/characters`);
      if (!response.ok) {
        throw new Error(`Request failed (HTTP ${response.status})`);
      }
      const data = await response.json();
      setCharacters(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching characters:', err);
      setError(err.message || 'Failed to load characters.');
    } finally {
      setLoading(false);
    }
  };

  /* Search + sort pipeline. Memoized so re-renders from unrelated state
     (e.g. a nested MoveRow GIF toggle) don't re-filter the whole list. */
  const visibleCharacters = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sort = SORT_OPTIONS.find(s => s.id === sortId) || SORT_OPTIONS[0];
    const filtered = q
      ? characters.filter(c => c.name.toLowerCase().includes(q))
      : characters.slice();
    return filtered.sort(sort.fn);
  }, [characters, query, sortId]);

  const renderControls = () => (
    <ControlsBar>
      <BackLink to="/">
        <FaArrowLeft />
        Home
      </BackLink>
      <SearchWrap>
        <FaSearch />
        <SearchInput
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search fighters…"
          aria-label="Search roster"
        />
      </SearchWrap>
      <SortWrap>
        <SortLabel>Sort</SortLabel>
        <SortSelect
          value={sortId}
          onChange={(e) => setSortId(e.target.value)}
          aria-label="Sort roster"
        >
          {SORT_OPTIONS.map(opt => (
            <option key={opt.id} value={opt.id}>{opt.label}</option>
          ))}
        </SortSelect>
      </SortWrap>
    </ControlsBar>
  );

  if (loading) {
    return (
      <CharacterPageContainer>
        <PageHeader>
          <PageTitle>Fighter roster</PageTitle>
          <PageSubtitle>Loading fighters…</PageSubtitle>
        </PageHeader>
        <LoadingSpinner />
      </CharacterPageContainer>
    );
  }

  if (error) {
    return (
      <CharacterPageContainer>
        <PageHeader>
          <PageTitle>Fighter roster</PageTitle>
          <PageSubtitle>Can't reach the server</PageSubtitle>
        </PageHeader>
        {renderControls()}
        <StatusPanel>
          <StatusIcon><FaExclamationTriangle /></StatusIcon>
          <StatusTitle>Connection lost</StatusTitle>
          <StatusDesc>{error}</StatusDesc>
          <RetryButton type="button" onClick={fetchCharacters}>
            <FaRedo /> Retry
          </RetryButton>
        </StatusPanel>
      </CharacterPageContainer>
    );
  }

  return (
    <CharacterPageContainer>
      <PageHeader>
        <PageTitle>Fighter roster</PageTitle>
        <PageSubtitle>
          {characters.length} fighters • stats, move caps, and what each move does
        </PageSubtitle>
      </PageHeader>

      <RosterGuideBlock />

      {renderControls()}

      {visibleCharacters.length === 0 ? (
        <StatusPanel>
          <StatusIcon style={{
            background: 'rgba(255, 177, 60, 0.12)',
            color: 'var(--color-amber-500)',
            borderColor: 'var(--color-amber-500)',
          }}>
            <FaSearch />
          </StatusIcon>
          <StatusTitle>No fighters match</StatusTitle>
          <StatusDesc>Try a different search — we couldn't find anyone called "{query}".</StatusDesc>
          <RetryButton type="button" onClick={() => setQuery('')}>
            Clear search
          </RetryButton>
        </StatusPanel>
      ) : (
        <CharacterGrid>
          {visibleCharacters.map((character, index) => (
            <CharacterCardComponent
              key={character._id}
              character={character}
              index={index}
            />
          ))}
        </CharacterGrid>
      )}
    </CharacterPageContainer>
  );
};

export default CharacterListPage;
